// Single Event PDF Report Generator
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ImageModal Integration - Updated component
import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogActions, 
  DialogContent, 
  Typography, 
  IconButton,
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Tooltip
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import { updateEventPriority } from '../../utils/EventService';

// Event type mapping for display
const EVENT_TYPE_MAP = {
  'CD': 'Crowd Detection',
  'VD': 'Vehicle Detection', 
  'PD': 'Person Detection',
  'ID': 'Intrusion Detection',
  'VC': 'Traffic Congestion',
  'LS': 'Loaded Vehicle',
  'US': 'Unloaded Vehicle',
  'CT': 'Camera Tampering',
  'SH': 'Safety Hazard',
  'II': 'Insufficient Illumination'
};

// Image fetch configuration
const IMAGE_FETCH_TIMEOUT = 8000;

/**
 * Fetch image as base64 with timeout and error handling
 */
const fetchImageAsBase64 = async (imageUrl, timeout = IMAGE_FETCH_TIMEOUT) => {
  try {
    console.log(`🖼️ Fetching single event image: ${imageUrl}`);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    const response = await fetch(imageUrl, {
      method: 'GET',
      signal: controller.signal,
      mode: 'cors',
      headers: {
        'Accept': 'image/*',
        'Cache-Control': 'no-cache'
      }
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      console.warn(`⚠️ Image fetch failed with status ${response.status}: ${imageUrl}`);
      return null;
    }
    
    const blob = await response.blob();
    
    if (!blob.type.startsWith('image/')) {
      console.warn(`⚠️ Response is not an image: ${blob.type} for URL: ${imageUrl}`);
      return null;
    }
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log(`✅ Successfully fetched single event image: ${imageUrl}`);
        resolve(reader.result);
      };
      reader.onerror = () => reject(new Error('Failed to convert blob to base64'));
      reader.readAsDataURL(blob);
    });
    
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn(`⚠️ Image fetch timeout: ${imageUrl}`);
    } else {
      console.warn(`⚠️ Failed to fetch image ${imageUrl}:`, error.message);
    }
    return null;
  }
};

/**
 * Format date for display in Indian timezone
 */
const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata'
    });
  } catch (error) {
    console.warn(`⚠️ Date formatting error for ${dateString}:`, error);
    return dateString || 'Unknown';
  }
};

/**
 * Generate image URL for the event
 */
const generateImageUrl = (event) => {
  let serverUrl = event.httpsUrl;
  let eventId = event.eventId || event.id;

  if (!serverUrl || !eventId) {
    console.warn(`⚠️ Cannot generate image URL:`, {
      serverUrl,
      eventId,
      event
    });
    return null;
  }
  
  const imageUrl = `${serverUrl}/api/va/event?id=${eventId}`;
  console.log(`🔗 Generated image URL for single event: ${imageUrl}`);
  
  return imageUrl;
};

/**
 * Generate PDF report for a single event
 */
export const generateSingleEventPDF = async (event, area, location, onProgress = null) => {
  try {
    console.log('🚀 Starting single event PDF generation...', event);
    
    if (onProgress) {
      onProgress({ 
        stage: 'processing', 
        percentage: 10, 
        message: 'Processing event data...' 
      });
    }

    // Extract event details
    const eventArea = area || event.area || event.attributes?.area || 'Unknown';
    const eventLocation = location || event.location || event.attributes?.location || 'Unknown';
    const eventTime = formatDate(event.timestamp || event.eventTime || event.createdAt || new Date().toISOString());
    const eventType = EVENT_TYPE_MAP[event.detectionType || event.eventType] || 
                     (event.detectionType || event.eventType || 'Unknown');
    const eventId = event.eventId || event.id || 'Unknown';
    const cameraIp = event.cameraIp || 'Unknown';
    const severity = event.severity || 'Normal';
    const remarks = event.remarks || [];

    if (onProgress) {
      onProgress({ 
        stage: 'fetching_image', 
        percentage: 30, 
        message: 'Fetching event image...' 
      });
    }

    // Try to fetch the event image
    let imageBase64 = null;
    const imageUrl = generateImageUrl(event);
    
    if (imageUrl) {
      imageBase64 = await fetchImageAsBase64(imageUrl);
    }

    if (onProgress) {
      onProgress({ 
        stage: 'generating_pdf', 
        percentage: 60, 
        message: 'Creating PDF document...' 
      });
    }

    // Create PDF document
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;

    // Add header
    pdf.setFontSize(20);
    pdf.setTextColor(25, 25, 112);
    pdf.setFont("helvetica", "bold");
    pdf.text('ICCC VA Event Report', pageWidth / 2, 25, { align: 'center' });
    
    pdf.setFontSize(17);
    pdf.setTextColor(220, 20, 60);
    pdf.setFont("helvetica", "bold");
    pdf.text('Event Details', pageWidth / 2, 35, { align: 'center' });

    // Add event summary box
    let currentY = 50;
    
    pdf.setFillColor(240, 248, 255);
    pdf.setDrawColor(70, 130, 180);
    pdf.rect(margin, currentY, pageWidth - 2 * margin, 60, 'FD');
    
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    
    // Event details in the summary box
    const summaryY = currentY + 5;
    pdf.text(`Area: ${eventArea}`, margin + 5, summaryY + 8);
    pdf.text(`Location: ${eventLocation}`, margin + 5, summaryY + 16);
    pdf.text(`Event Type: ${eventType}`, margin + 5, summaryY + 24);
    pdf.text(`Camera IP: ${cameraIp}`, margin + 5, summaryY + 32);
    pdf.text(`Event Time: ${eventTime}`, margin + 5, summaryY + 40);
    pdf.text(`Severity: ${severity}`, margin + 5, summaryY + 48);

    currentY += 70;

    // Add event image if available
    if (imageBase64) {
      if (onProgress) {
        onProgress({ 
          stage: 'adding_image', 
          percentage: 80, 
          message: 'Adding event image...' 
        });
      }

      try {
        pdf.setFontSize(14);
        pdf.setTextColor(25, 25, 112);
        pdf.text('Event Image:', margin, currentY);
        currentY += 10;

        // Calculate image dimensions to fit on page
        const maxImageWidth = pageWidth - 2 * margin;
        const maxImageHeight = 120; // Reserve space for comments below
        
        // Add border around image
        pdf.setDrawColor(0, 0, 0);
        pdf.rect(margin, currentY, maxImageWidth, maxImageHeight);
        
        // Add image with some padding
        const imagePadding = 2;
        pdf.addImage(
          imageBase64, 
          'JPEG', 
          margin + imagePadding, 
          currentY + imagePadding, 
          maxImageWidth - 2 * imagePadding, 
          maxImageHeight - 2 * imagePadding
        );
        
        currentY += maxImageHeight + 15;
      } catch (imgError) {
        console.error(`❌ Error adding image to PDF:`, imgError);
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        pdf.text('Image could not be loaded', margin, currentY);
        currentY += 15;
      }
    } else {
      pdf.setFontSize(12);
      pdf.setTextColor(128, 128, 128);
      pdf.text('Event Image: Not Available', margin, currentY);
      currentY += 20;
    }

    // Add comments section
    if (remarks && remarks.length > 0) {
      // Check if we need a new page
      if (currentY > pageHeight - 80) {
        pdf.addPage();
        currentY = 20;
      }

      pdf.setFontSize(14);
      pdf.setTextColor(25, 25, 112);
      pdf.text('Comments & Remarks:', margin, currentY);
      currentY += 10;

      remarks.forEach((remark, index) => {
        // Check if we need a new page for this comment
        if (currentY > pageHeight - 40) {
          pdf.addPage();
          currentY = 20;
        }

        // Comment box
        const commentHeight = 25;
        pdf.setFillColor(248, 248, 255);
        pdf.setDrawColor(200, 200, 200);
        pdf.rect(margin, currentY, pageWidth - 2 * margin, commentHeight, 'FD');
        
        pdf.setFontSize(10);
        pdf.setTextColor(70, 130, 180);
        pdf.text(`${remark.user || 'Unknown User'}`, margin + 3, currentY + 8);
        
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        const timestamp = remark.timestamp ? formatDate(remark.timestamp) : 'No timestamp';
        pdf.text(timestamp, pageWidth - margin - 3, currentY + 8, { align: 'right' });
        
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        const commentLines = pdf.splitTextToSize(remark.comment || 'No comment', pageWidth - 2 * margin - 6);
        pdf.text(commentLines, margin + 3, currentY + 16);
        
        currentY += commentHeight + 5;
      });
    } else {
      pdf.setFontSize(12);
      pdf.setTextColor(128, 128, 128);
      pdf.text('Comments: No comments available', margin, currentY);
      currentY += 20;
    }

    // Add footer with generation info
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    const footerY = pageHeight - 15;
    pdf.text(`Report generated on: ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}`, margin, footerY);
    pdf.text(`ICCC VA System - Single Event Report`, pageWidth - margin, footerY, { align: 'right' });

    if (onProgress) {
      onProgress({ 
        stage: 'complete', 
        percentage: 100, 
        message: 'PDF generated successfully!' 
      });
    }

    // Generate filename
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
    const sanitizedArea = eventArea.replace(/[^a-zA-Z0-9]/g, '_');
    const sanitizedLocation = eventLocation.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `ICCC_Event_${eventId}_${sanitizedArea}_${sanitizedLocation}_${timestamp}.pdf`;
    
    // Download PDF
    pdf.save(filename);
    
    console.log(`✅ Single event PDF report generated successfully: ${filename}`);
    
    return { 
      success: true, 
      filename,
      hasImage: !!imageBase64,
      eventId: eventId
    };
    
  } catch (error) {
    console.error('❌ Single event PDF generation failed:', error);
    
    if (onProgress) {
      onProgress({ 
        stage: 'error', 
        percentage: 0, 
        message: `Error: ${error.message}` 
      });
    }
    
    throw error;
  }
};